import { ApiError } from '@novachat/client';
import type { Attachment } from '@novachat/shared';
import { apiUrl, client } from './client';

/** Signed file links are relative to the API; local previews (blob:, data:) stay as they are. */
export const fileUrl = (path: string) => (path.startsWith('/') ? `${apiUrl}${path}` : path);

/** "2,4 MB" */
export function sizeText(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1).replace('.', ',')} MB`;
}

/** PUT /channels/:id/logo with a picture; the server makes it a 256 px square. */
export async function uploadChannelLogo(channelId: string, file: File): Promise<void> {
  const token = await client.session.accessToken();
  if (!token) throw new ApiError(401, 'UNAUTHORIZED', 'Signed out');
  const form = new FormData();
  form.append('file', file, file.name);
  const response = await fetch(`${apiUrl}/channels/${channelId}/logo`, { method: 'PUT', headers: { authorization: `Bearer ${token}` }, body: form });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { code?: string; message?: string };
    throw new ApiError(response.status, body.code ?? 'HTTP_ERROR', body.message ?? `HTTP ${response.status}`);
  }
}

/** XHR rather than fetch: only XHR reports upload progress. */
export async function uploadFile(channelId: string, file: File, onProgress: (share: number) => void, signal: AbortSignal): Promise<Attachment> {
  const token = await client.session.accessToken();
  if (!token) throw new ApiError(401, 'UNAUTHORIZED', 'Signed out');
  return new Promise<Attachment>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiUrl}/channels/${channelId}/attachments`);
    xhr.setRequestHeader('authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      let body: { code?: string; message?: string } = {};
      try {
        body = JSON.parse(xhr.responseText) as typeof body;
      } catch {
        // proxies answer 413 with HTML
      }
      if (xhr.status === 201) resolve(body as unknown as Attachment);
      else reject(new ApiError(xhr.status, body.code ?? (xhr.status === 413 ? 'PAYLOAD_TOO_LARGE' : 'HTTP_ERROR'), body.message ?? `HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new ApiError(0, 'NETWORK_ERROR', 'Network error'));
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));
    signal.addEventListener('abort', () => xhr.abort(), { once: true });
    const form = new FormData();
    form.append('file', file, file.name);
    xhr.send(form);
  });
}
